<template>
  <svg :x="plaqueX" :y="plaqueY" :width="`${plaqueWidth}in`" :height="`${plaqueHeight}in`" xmlns="http://www.w3.org/2000/svg">
    <image
      :href="plaqueImageUrl"
      :width="plaqueWidth"
      :height="plaqueHeight"
      preserveAspectRatio="none"
    />
    <text
      v-if="lines.header1"
      class="header1"
      :font-size="pxToInches(lines.header1.fontSize)"
      :letter-spacing="0.002"
      :font-family="lines.header1.fontFamily"
      :font-weight="lines.header1.fontWeight"
      :fill="textColor"
      text-anchor="middle"
      :x="matPlaqueTextX"
      :y="lines.header1.top + textBoxTop"
      dominant-baseline="middle"
    >
      {{ lines.header1.value }}
    </text>
    <text
      v-if="lines.header2"
      class="header2"
      :font-size="pxToInches(lines.header2.fontSize)"
      :letter-spacing="0.002"
      :font-family="lines.header2.fontFamily"
      :font-weight="lines.header2.fontWeight"
      :fill="textColor"
      text-anchor="middle"
      :x="matPlaqueTextX"
      :y="lines.header2.top + textBoxTop"
      dominant-baseline="middle"
    >
      {{ lines.header2.value }}
    </text>
    <text
      v-if="lines.subheader1"
      class="subheader1"
      :font-size="pxToInches(lines.subheader1.fontSize)"
      :letter-spacing="0.002"
      :font-family="lines.subheader1.fontFamily"
      :font-weight="lines.subheader1.fontWeight"
      :fill="textColor"
      text-anchor="middle"
      :x="matPlaqueTextX"
      :y="lines.subheader1.top + textBoxTop"
      dominant-baseline="middle"
    >
      {{ lines.subheader1.value }}
    </text>
    <text
      v-if="lines.subheader2"
      class="subheader2"
      :font-size="pxToInches(lines.subheader2.fontSize)"
      :letter-spacing="0.002"
      :font-family="lines.subheader2.fontFamily"
      :font-weight="lines.subheader2.fontWeight"
      :fill="textColor"
      text-anchor="middle"
      :x="matPlaqueTextX"
      :y="lines.subheader2.top + textBoxTop"
      dominant-baseline="middle"
    >
      {{ lines.subheader2.value }}
    </text>
    <template v-if="matPlaqueParagraph">
      <text
        v-for="(line, index) in matPlaqueParagraph.lines"
        :key="`paragraph_${index}`"
        :font-size="pxToInches(matPlaqueParagraph.fontSize || 20)"
        :letter-spacing="0.002"
        :font-family="matPlaqueParagraph.fontStyle"
        :font-weight="'normal'"
        :fill="textColor"
        text-anchor="middle"
        :x="matPlaqueTextX"
        :y="(lines.paragraph_0 ? lines.paragraph_0.top : 0) + textBoxTop + (index * (lines.paragraph_0 ? lines.paragraph_0.height : pxToInches(matPlaqueParagraph.fontSize || 20) * 1.2))"
        dominant-baseline="middle"
      >
        {{ line }}
      </text>
    </template>
    <text
      v-if="lines.signature"
      class="signature"
      :font-size="pxToInches(lines.signature.fontSize)"
      :letter-spacing="0.002"
      :font-family="lines.signature.fontFamily"
      :font-weight="lines.signature.fontWeight"
      :fill="textColor"
      text-anchor="middle"
      :x="matPlaqueTextX"
      :y="lines.signature.top + textBoxTop"
      dominant-baseline="middle"
    >
      {{ lines.signature.value }}
    </text>
  </svg>
</template>

<script>
export default {
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    plaqueX: {
      type: Number,
      required: true
    },
    plaqueY: {
      type: Number,
      required: true
    },
    plaqueWidth: {
      type: Number,
      required: true
    },
    plaqueHeight: {
      type: Number,
      required: true
    }
  },
  computed: {
    matPlaque () {
      return this.frameSpec && this.frameSpec.matPlaque;
    },
    isSilver () {
      return this.matPlaque && this.matPlaque.sku && this.matPlaque.sku.endsWith('S');
    },
    plaqueImageUrl() {
      if (this.matPlaque && this.matPlaque.templateLayout === 'Personalized Layout') {
        return this.isSilver ? 'https://cdn.shopify.com/s/files/1/0769/3602/0283/files/BlankPlaqueforRendering_SILVER_withshadow_custom.png?v=1752246160'
          : 'https://cdn.shopify.com/s/files/1/0769/3602/0283/files/BlankPlaqueforRendering_GOLD_withshadow_custom.png?v=1752246160';
      }
      return this.isSilver ? 'https://cdn.shopify.com/s/files/1/0769/3602/0283/files/BlankPlaqueforRendering_SILVER_withshadow.png?v=1752151538'
        : 'https://cdn.shopify.com/s/files/1/0769/3602/0283/files/BlankPlaqueforRendering_GOLD_withshadow.png?v=1752151528';
    },
    textColor () {
      return this.isSilver ? '#CFCFCF' : '#D4BF88';
    },
    matPlaqueTextX() {
      return this.plaqueWidth / 2;
    },
    matPlaqueParagraph () {
      const matPlaqueParagraph = (this.frameSpec && this.frameSpec.matPlaque && this.frameSpec.matPlaque.bodyCopy) || (this.frameSpec && this.frameSpec.matPlaque && this.frameSpec.matPlaque.body);
      if (!(matPlaqueParagraph && (matPlaqueParagraph.text || matPlaqueParagraph.copy))) {
        return undefined;
      }
      const text = matPlaqueParagraph.text || matPlaqueParagraph.copy;
      const charLimit = 50; // we allow up to 7 lines and total of 350 characters. sets a per line limit of 50, matches signature line length.
      function wrapLine(line, limit) {
        const result = [];
        let current = line;
        while (current.length > limit) {
          let breakAt = current.lastIndexOf(' ', limit);
          if (breakAt === -1) breakAt = limit;
          result.push(current.slice(0, breakAt));
          current = current.slice(breakAt).trimStart();
        }
        if (current.length) result.push(current);
        return result;
      }
      // Split by line breaks, do not preserve empty lines for extra spacing
      const lines = text.split('\n').flatMap(line => wrapLine(line, charLimit));
      return {
        text,
        lines,
        ...matPlaqueParagraph
      };
    },
    lines () {
      const gap = this.pxToInches(10);
      // Group lines into blocks: header, subheader, paragraph, signature
      const blocks = [];
      // Header block
      const headerLines = [];
      if (this.headerLine1) headerLines.push({ key: 'header1', value: this.headerLine1, fontSize: this.matPlaque.header?.fontSize, fontFamily: this.matPlaque.header?.fontStyle, fontWeight: this.headerFontWeight });
      if (this.headerLine2) headerLines.push({ key: 'header2', value: this.matPlaque.header?.line2, fontSize: this.matPlaque.header?.fontSize, fontFamily: this.matPlaque.header?.fontStyle, fontWeight: this.headerFontWeight });
      if (headerLines.length) {
        blocks.push({ type: 'header', lines: headerLines, lineHeight: this.getLineHeight(this.matPlaque.header?.fontSize, this.matPlaque.header?.fontStyle === 'bickham-script-pro-3') });
      }
      // Subheader block
      const subheaderLines = [];
      if (this.subheaderLine1) subheaderLines.push({ key: 'subheader1', value: this.matPlaque.subheader?.line1, fontSize: this.matPlaque.subheader?.fontSize, fontFamily: this.matPlaque.subheader?.fontStyle, fontWeight: 'bold' });
      if (this.subheaderLine2) subheaderLines.push({ key: 'subheader2', value: this.matPlaque.subheader?.line2, fontSize: this.matPlaque.subheader?.fontSize, fontFamily: this.matPlaque.subheader?.fontStyle, fontWeight: 'bold' });
      if (subheaderLines.length) {
        blocks.push({ type: 'subheader', lines: subheaderLines, lineHeight: this.getLineHeight(this.matPlaque.subheader?.fontSize) });
      }
      // Paragraph block
      if (this.matPlaqueParagraph && this.matPlaqueParagraph.lines.length) {
        // Use a default font size if not set or invalid (restore to 20px)
        const defaultParagraphFontSize = 20;
        let paragraphFontSize = parseFloat(this.matPlaqueParagraph.fontSize);
        if (!paragraphFontSize || isNaN(paragraphFontSize)) paragraphFontSize = defaultParagraphFontSize;
        const paragraphLines = this.matPlaqueParagraph.lines.map((line, idx) => ({
          key: `paragraph_${idx}`,
          value: line,
          fontSize: paragraphFontSize,
          fontFamily: this.matPlaqueParagraph.fontStyle,
          fontWeight: 'normal'
        }));
        blocks.push({ type: 'paragraph', lines: paragraphLines, lineHeight: this.getLineHeight(paragraphFontSize) });
      }
      // Signature block
      if (this.matPlaque && this.matPlaque.signature && this.matPlaque.signature.line) {
        blocks.push({ type: 'signature', lines: [{ key: 'signature', value: this.matPlaque.signature.line, fontSize: this.matPlaque.signature.fontSize, fontFamily: this.matPlaque.signature.fontStyle, fontWeight: 'normal' }], lineHeight: this.getLineHeight(this.matPlaque.signature.fontSize) });
      }
      if (blocks.length === 0) return {};
      // Calculate total block height
      const blockHeight = blocks.reduce((sum, block, idx) => sum + (block.lines.length * block.lineHeight) + (idx > 0 ? gap : 0), 0);
      // Start stacking from top (let textBoxTop handle centering)
      let currentTop = 0;
      const out = {};
      blocks.forEach((block, blockIdx) => {
        block.lines.forEach((lineObj, lineIdx) => {
          out[lineObj.key] = {
            top: currentTop + (lineIdx * block.lineHeight),
            fontSize: lineObj.fontSize,
            fontFamily: lineObj.fontFamily,
            fontWeight: lineObj.fontWeight,
            value: lineObj.value,
            lineIdx,
            blockType: block.type,
            height: block.lineHeight // <-- add height for each line
          };
        });
        currentTop += block.lines.length * block.lineHeight + gap;
      });
      return out;
    },
    textBoxHeight () {
      // Sum the heights of all visible lines
      const linesArr = Object.values(this.lines);
      if (!linesArr.length) return 0;
      // Find the min and max top positions
      const minTop = Math.min(...linesArr.map(l => l.top));
      const maxTop = Math.max(...linesArr.map(l => l.top));
      // The height is from minTop to maxTop plus the height of the last line
      const lastLine = linesArr.find(l => l.top === maxTop);
      return (maxTop - minTop) + (lastLine ? lastLine.height : 0);
    },
    textBoxTop () {
      const offsetTop = 0.1; // viewbox needs to be fixed
      return (this.plaqueHeight - this.textBoxHeight) / 2 + offsetTop;
    },
    headerFontWeight () {
      return this.matPlaque && this.matPlaque.header && this.matPlaque.header.fontStyle === 'apercu' ? 'bold' : 'normal';
    },
    headerLine1() {
      return this.matPlaque && this.matPlaque.header && this.matPlaque.header.line1;
    },
    headerLine2() {
      return this.matPlaque && this.matPlaque.header && this.matPlaque.header.line2;
    },
    subheaderLine1() {
      return this.matPlaque && this.matPlaque.subheader && this.matPlaque.subheader.line1;
    },
    subheaderLine2() {
      return this.matPlaque && this.matPlaque.subheader && this.matPlaque.subheader.line2;
    }
  },
  methods: {
    pxToInches (px) {
      // pixels per inch = 270px / 3.75in
      // this was calculated using the proportions from figma designs to match the font sizes
      const PPI = 72;

      return parseFloat(px) / PPI;
    },
    getLineHeight (fontSize, sameAsFontSize = false) {
      return this.pxToInches(fontSize) * (sameAsFontSize ? 0.8 : 1.2);
    }
  }
};
</script>

<style scoped>
/* No styles needed */
</style>
